import { ChangeEvent, FunctionComponent, useEffect, useRef, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import * as yup from "yup";
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import alertIcon from "../../assets/dropdown/alert-icon-error.svg";
import InputsNew from '../../components/common/InputsNew';
import DateSelector from '../../components/common/DateSelector';
import { showError, showSuccess } from '../../components/common/ToastNotification';
import { ApolloError, useLazyQuery, useMutation, useQuery } from '@apollo/client';
import { GOAL_DETAILS, GOAL_MASTER_DATA, GET_RESOURCE_OVERVIEW, GET_ALL_SKILLS } from '../../api/query';
import Loader from '../../components/common/Loader';
import { CREATE_GOAL } from '../../api/mutation';
import closeIcon from "../../assets/task/close.png";
import rePlacement from "../../assets/task/user-account.png";
import { useLocation, useNavigate } from 'react-router-dom'
import BreadCumb from '../../components/common/Breadcrumb';
import backArrow from "../../assets/task/back_arrow.svg";
import AddMemberToGoalPopup from '../../components/goals/AddMemberToGoalPopup';
import ReplaceMemberPopup from '../../components/goals/ReplaceMemberPopup';
import NotifyPopup from '../../components/dashboard/NotifyPopup';
import { uploadFileDirectly } from "../projects/AddProject";
import viewFileIon from "../../assets/task/vewidea-iconfile.svg";
import ioniconaaddoutline from "../../assets/dashboard/ioniconaaddoutline.svg";
import { LinkItem, LinkManager } from "../../components/customerSuccess/LinkManager";
import { differenceInDays } from 'date-fns';
import InputsDropdownMultipleSearchSelect from '../../components/common/InputsDropdownMultipleSearchSelect';
import closeDowpDowniconimg from "../../assets/task/closedropdown-cicon.svg";

const EditGoal: FunctionComponent = () => {
    const { userInfo } = useAuth()
    const navigate = useNavigate();
    const employeeID = userInfo?.employee_id || '';
    const organizationID = userInfo?.agencies?.id || '';
    const [goalMemberOptions, setGoalMemberOptions] = useState<{ key: string; name: string; isAvailable?: boolean; availableHours?: number; totalHours?: number; utilizationPercentage?: number }[]>([]);
    const [selectedMembers, setSelectedMembers] = useState<any[]>([]);
    const [originalMembers, setOriginalMembers] = useState<any[]>([]); // Track original members
    const [showAddMemberPopup, setShowAddMemberPopup] = useState(false);
    const [showReplaceMemberPopup, setShowReplaceMemberPopup] = useState(false);
    const [memberToReplace, setMemberToReplace] = useState<{ key: string; name: string } | null>(null);
    const [goalTypeData, setGoalTypeData] = useState<any>(null);
    const [memberTasks, setMemberTasks] = useState<{ [memberKey: string]: any[] }>({});
    const [editingMember, setEditingMember] = useState<{ key: string; name: string } | null>(null);
    const [taskReplacements, setTaskReplacements] = useState<{ [taskId: string]: string }>({});
    const [showRemoveConfirmation, setShowRemoveConfirmation] = useState(false);
    const [memberToRemove, setMemberToRemove] = useState<{ key: string; name: string; reassignedTaskCount: number } | null>(null);

    // Attachments and Links state
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [goalAttachmentView, setGoalAttachmentView] = useState("attachments");
    const [imgUrls, setImgUrls] = useState<string[]>([]);
    const [fileNames, setFileNames] = useState<string[]>([]);
    const [fileTypes, setFileTypes] = useState<string[]>([]);
    const [existingAttachments, setExistingAttachments] = useState<
        { id: string; file_path: string; file_name: string; file_type: string }[]
    >([]);
    const [isUploading, setIsUploading] = useState<boolean>(false);
    const [validationMessage, setValidationMessage] = useState<string | null>(null);
    const [links, setLinks] = useState<LinkItem[]>([]);

    // Skills state
    const [skillOptions, setSkillOptions] = useState<{ key: string; name: string }[]>([]);
    const [selectedSkills, setSelectedSkills] = useState<any[]>([]);
    
    // Parent task options for existing goal
    const [parentTaskOptions, setParentTaskOptions] = useState<{ key: string; name: string }[]>([]);

    const location = useLocation()
    const state = location.state as {
        projectName?: string,
        projectID: string,
        goalID: string,
        goalMembers?: any,
        projectMembers?: any
    };

    const { projectName, projectID, goalID, goalMembers, projectMembers } = state

    // Validation schema
    const addTaskSchema = yup.object().shape({
        goalName: yup.string()
            .test('not-only-whitespace', 'Please add a valid Goal Name', value => !value || value.trim().length > 0)
            .required('This field is required'),
        startGoalDate: yup.string().required("This field is required"),
        endGoalDate: yup.string().required("This field is required"),
        goalDescription: yup.string()
            .test('not-only-whitespace', 'Please add a valid Goal Description', value => !value || value.trim().length > 0).nullable(),
    });

    //api
    const { data: skillData, loading: skillLoading } = useQuery(GET_ALL_SKILLS, {
        fetchPolicy: 'no-cache',
    });

    const [
        getGoalDetails,
        { data: goalData, loading: goalDataLoading },
    ] = useLazyQuery(GOAL_DETAILS, {
        fetchPolicy: 'no-cache'
    });

    const [
        getGoalMasterData,
        { data: goalsData, loading: goalsDataLoading },
    ] = useLazyQuery(GOAL_MASTER_DATA, {
        fetchPolicy: 'no-cache'
    });

    useEffect(() => {
        if (goalID) {
            getGoalDetails({
                variables: {
                    goal_id: parseInt(goalID)
                }
            })
        }

    }, [goalID])

    const [createGoal, { loading: createGoalLoading }] = useMutation(CREATE_GOAL, {
        fetchPolicy: 'no-cache',
    })

    const [
        getResourceOverview,
        { data: resourceOverviewData, loading: resourceOverviewLoading },
    ] = useLazyQuery(GET_RESOURCE_OVERVIEW, {
        fetchPolicy: 'no-cache'
    });

    const {
        register,
        handleSubmit,
        setValue,
        formState: { errors },
        trigger,
        watch,
        control
    } = useForm<any>({
        resolver: yupResolver(addTaskSchema),
        defaultValues: {
            assignToOther: false
        }
    });

    //watch
    const watchStartGoalDate: string = watch('startGoalDate');
    const watchEndGoalDate: string = watch('endGoalDate');

    // Check if all tasks are "Not Started" to allow end date editing
    const areAllTasksNotStarted = () => {
        const tasks = goalData?.goal_details?.goal_details?.tasks;
        if (!tasks || tasks.length === 0) {
            return true; // If no tasks, allow editing
        }
        
        return tasks.every((task: any) => {
            const statusName = task.status_name?.toLowerCase().trim() || '';
            return statusName === 'not started' || 
                   statusName === 'notstarted' || 
                   statusName === 'not_started' ||
                   statusName === '';
        });
    };

    const isEndDateEditable = areAllTasksNotStarted();

    // Skills useEffect
    useEffect(() => {
        if (skillData && skillData.get_all_skills?.skills?.length > 0) {
            const options = skillData.get_all_skills?.skills?.map((skill: any) => ({
                key: skill.id,
                name: skill?.skill_name
            }));
            setSkillOptions(options);
        }
    }, [skillData])

    useEffect(() => {
        if (goalData) {
            const goal = goalData?.goal_details?.goal_details
            setValue('goalName', goal?.title)
            setValue('startGoalDate', goal?.start_date?.split("T")[0])
            setValue('endGoalDate', goal?.end_date?.split("T")[0])
            setValue('goalDescription', goal?.description)

            const memberOptions = goalMembers?.map((m: any) => ({
                key: m.employee_id,
                name: m.member_name
            }));

            setSelectedMembers(memberOptions);
            setOriginalMembers(memberOptions || []); // Store original members

            // Populate parent task options from existing goal tasks
            if (goal?.tasks?.length > 0) {
                const taskOptions = goal.tasks.map((t: any) => ({
                    key: t.id,
                    name: t.title
                }));
                setParentTaskOptions(taskOptions);
            }

            // Fetch goal master data to get task types
            if (goal?.static_goal_type_name) {
                getGoalMasterData();
            }

            // Handle attachments from existing goal
            if (goal?.attachments?.length > 0) {
                const attachments = goal.attachments;
                const fileUrls = attachments.map((attachment: any) => attachment.file_path);
                const fileNames = attachments.map((attachment: any) => attachment.file_name);
                const fileTypes = attachments.map((attachment: any) => attachment.file_type);

                setImgUrls(fileUrls);
                setFileNames(fileNames);
                setFileTypes(fileTypes);

                setExistingAttachments(attachments.map((a: any) => ({
                    id: a.id,
                    file_path: a.file_path,
                    file_name: a.file_name,
                    file_type: a.file_type
                })));
            } else {
                // Clear attachments if goal has none
                setImgUrls([]);
                setFileNames([]);
                setFileTypes([]);
                setExistingAttachments([]);
            }

            // Handle links from existing goal
            if (goal?.links?.length > 0) {
                setLinks(goal?.links?.map((l: any) => ({
                    id: Date.now().toString(),
                    url: l
                })));
            } else {
                // Clear links if goal has none
                setLinks([]);
            }

            // Handle skills from existing goal
            if (goal?.skills?.length > 0) {
                const skillOptions = goal?.skills?.map((s: any) => ({
                    key: s?.skill_id,
                    name: s?.skill_name
                }));
                setSelectedSkills(skillOptions);
            } else {
                // Clear skills if goal has none
                setSelectedSkills([]);
            }
        }
    }, [goalData])

    useEffect(() => {
        if (goalMembers) {
            const memberOptions = goalMembers?.map((m: any) => ({
                key: m.employee_id,
                name: m.member_name
            }));

            setSelectedMembers(memberOptions);
            setOriginalMembers(memberOptions || []); // Also store original members here
        }
    }, [state])

    useEffect(() => {
        if (goalsData?.goal_master_data?.goals_static) {

            const goalTypeInfo = goalsData.goal_master_data.goals_static.find((goal: any) =>
                goal.name === goalData?.goal_details?.goal_details?.static_goal_type_name
            );
            setGoalTypeData(goalTypeInfo);
        }
    }, [goalsData, goalData]);

    // Call resource overview API when goal dates are available
    useEffect(() => {
        if (watchStartGoalDate && watchEndGoalDate && projectID && organizationID) {
            const input = {
                agency_id: parseInt(organizationID),
                start_date: watchStartGoalDate,
                end_date: watchEndGoalDate,
                project_id: parseInt(projectID)
            };

            getResourceOverview({
                variables: {
                    input_data: input
                }
            });
        }
    }, [watchStartGoalDate, watchEndGoalDate, projectID, organizationID, getResourceOverview]);

    // Process resource overview data and update member options
    useEffect(() => {
        if (resourceOverviewData?.get_resource_overview?.status_code === 200) {
            const resourceData = resourceOverviewData.get_resource_overview.data;

            if (resourceData?.members && resourceData.members.length > 0) {
                // Sort members: available first, then non-available
                const sortedMembers = [...resourceData.members].sort((a, b) => {
                    if (a.is_available && !b.is_available) return -1;
                    if (!a.is_available && b.is_available) return 1;
                    return 0;
                });

                const memberOptions = sortedMembers.map((member: any) => ({
                    key: member.employee_id,
                    name: `${member.first_name} ${member.last_name}`,
                    isAvailable: member.is_available,
                    availableHours: Math.max(0, member.total_working_hours - member.allocated_working_hours),
                    totalHours: member.total_working_hours,
                    utilizationPercentage: member.utilization_percentage
                }));

                setGoalMemberOptions(memberOptions);

                // Update selected members with availability data if they exist
                if (selectedMembers.length > 0) {
                    const updatedSelectedMembers = selectedMembers.map(selectedMember => {
                        const memberWithAvailability = memberOptions.find(m => m.key === selectedMember.key);
                        if (memberWithAvailability) {
                            return {
                                ...selectedMember,
                                isAvailable: memberWithAvailability.isAvailable,
                                availableHours: memberWithAvailability.availableHours,
                                totalHours: memberWithAvailability.totalHours,
                                utilizationPercentage: memberWithAvailability.utilizationPercentage
                            };
                        }
                        return selectedMember;
                    });
                    setSelectedMembers(updatedSelectedMembers);
                }
            } else {
                // No members available for the selected date range
                setGoalMemberOptions([]);
            }
        } else if (resourceOverviewData?.get_resource_overview?.status_code !== 200) {
            // API returned an error
            console.error('Resource overview API error:', resourceOverviewData?.get_resource_overview?.message);
            setGoalMemberOptions([]);
        }
    }, [resourceOverviewData]);

    const handleRemoveMember = (key: string) => {
        // Safety check: only allow removal of newly added members
        if (!isRemovableMember(key)) {
            showError('Cannot remove original goal members');
            return;
        }

        // Check if this member has any reassigned tasks
        const hasReassignedTasks = Object.values(taskReplacements).includes(key);
        if (hasReassignedTasks) {
            const reassignedTaskCount = Object.values(taskReplacements).filter(assigneeId => assigneeId === key).length;
            const memberName = selectedMembers.find(m => m.key === key)?.name || 'Unknown Member';

            // Show NotifyPopup for confirmation
            setMemberToRemove({
                key,
                name: memberName,
                reassignedTaskCount
            });
            setShowRemoveConfirmation(true);
            return;
        }

        // If no reassigned tasks, remove directly
        performMemberRemoval(key);
    };

    const performMemberRemoval = (key: string) => {
        setSelectedMembers((prev) => prev.filter((item) => item.key !== key));
        // Also remove tasks for this member
        setMemberTasks((prev) => {
            const newTasks = { ...prev };
            delete newTasks[key];
            return newTasks;
        });
    };

    const handleConfirmRemoveMember = () => {
        if (!memberToRemove) return;

        // Revert task assignments for this member
        const updatedReplacements = { ...taskReplacements };
        Object.keys(updatedReplacements).forEach(taskId => {
            if (updatedReplacements[taskId] === memberToRemove.key) {
                delete updatedReplacements[taskId];
            }
        });
        setTaskReplacements(updatedReplacements);

        // Remove the member
        performMemberRemoval(memberToRemove.key);

        // Show success message
        showSuccess(`Task assignments reverted and ${memberToRemove.name} removed`);

        // Close popup
        setShowRemoveConfirmation(false);
        setMemberToRemove(null);
    };

    const handleCancelRemoveMember = () => {
        setShowRemoveConfirmation(false);
        setMemberToRemove(null);
    };

    const handleAddMember = (memberData: any) => {
        if (editingMember) {
            // If editing and member changed, handle the reassignment
            if (editingMember.key !== memberData.member.key) {
                // Remove tasks from old member
                setMemberTasks((prev) => {
                    const newTasks = { ...prev };
                    delete newTasks[editingMember.key];
                    return newTasks;
                });

                // Remove old member from selectedMembers if they were newly added (not original)
                if (isRemovableMember(editingMember.key)) {
                    setSelectedMembers((prev) => prev.filter((member) => member.key !== editingMember.key));
                }

                // Add new member to selectedMembers if not already there
                setSelectedMembers((prev) => {
                    const exists = prev.some((member) => member.key === memberData.member.key);
                    if (!exists) {
                        return [...prev, memberData.member];
                    }
                    return prev;
                });
            }

            // Always update tasks for the selected member (new or same)
            setMemberTasks((prev) => ({
                ...prev,
                [memberData.member.key]: memberData.tasks
            }));
        } else {
            // Adding member or adding tasks to existing member
            setSelectedMembers((prev) => {
                const exists = prev.some((member) => member.key === memberData.member.key);
                if (!exists) {
                    // Add new member to selectedMembers
                    return [...prev, memberData.member];
                }
                // Member already exists, just return the current list
                return prev;
            });

            // Always update/add tasks for the member
            setMemberTasks((prev) => ({
                ...prev,
                [memberData.member.key]: memberData.tasks
            }));
        }

        setShowAddMemberPopup(false);
        setEditingMember(null);
    };

    const handleMemberClick = (member: any) => {
        // Check if this member has tasks
        const memberTaskList = memberTasks[member.key] || [];
        if (memberTaskList.length > 0) {
            setEditingMember(member);
            setShowAddMemberPopup(true);
        }
    };

    const getAvailableMembers = () => {
        // Return all project members with availability data
        return goalMemberOptions || [];
    };

    const isRemovableMember = (memberKey: string) => {
        // A member is removable if they are NOT in the original members list
        return !originalMembers.some(original => original.key === memberKey);
    };

    const handleReplaceMember = (member: any) => {
        setMemberToReplace(member);
        setShowReplaceMemberPopup(true);
    };

    const handleReplaceMemberSubmit = (data: {
        originalMember: { key: string; name: string };
        replacementMember: { key: string; name: string };
        selectedTasks: string[];
    }) => {
        // Update local state to track task replacements
        const newReplacements = { ...taskReplacements };
        data.selectedTasks.forEach(taskId => {
            newReplacements[taskId] = data.replacementMember.key;
        });
        setTaskReplacements(newReplacements);

        // Add replacement member to selectedMembers if not already there
        setSelectedMembers(prev => {
            const exists = prev.some(member => member.key === data.replacementMember.key);
            if (!exists) {
                return [...prev, data.replacementMember];
            }
            return prev;
        });

        showSuccess(`${data.selectedTasks.length} task(s) will be reassigned from ${data.originalMember.name} to ${data.replacementMember.name} when you save`);

        // Close the popup
        setShowReplaceMemberPopup(false);
        setMemberToReplace(null);
    };

    // Helper function to map API status to our expected format
    const mapTaskStatus = (statusName: string | null): 'Not Started' | 'In Progress' | 'Completed' => {
        if (!statusName) return 'Not Started';

        const statusLower = statusName.toLowerCase().trim();

        // Common status mappings
        if (statusLower.includes('progress') ||
            statusLower.includes('ongoing') ||
            statusLower.includes('active') ||
            statusLower.includes('working') ||
            statusLower.includes('started')) {
            return 'In Progress';
        } else if (statusLower.includes('completed') ||
            statusLower.includes('done') ||
            statusLower.includes('finished') ||
            statusLower.includes('closed') ||
            statusLower.includes('resolved')) {
            return 'Completed';
        } else {
            return 'Not Started';
        }
    };

    // File upload functions
    function handleIconClick() {
        fileInputRef.current?.click();
    }

    async function handleFileUpload(event: ChangeEvent<HTMLInputElement>) {
        const files = event.target.files;
        if (!files || files.length === 0) return;

        const token = localStorage.getItem("authToken")!;
        const validTypes = ["image/jpeg", "image/png"];
        const newUrls: string[] = [];
        const newNames: string[] = [];
        const newTypes: string[] = [];

        setIsUploading(true);
        setValidationMessage(null);

        for (const file of Array.from(files)) {
            // if (!validTypes.includes(file.type)) {
            //     setValidationMessage("Only JPG and PNG files are allowed.");
            //     setIsUploading(false);
            //     return;
            // }

            try {
                const url = await uploadFileDirectly(file, file.name, token);
                newUrls.push(url);
                newNames.push(file.name);
                newTypes.push(file.type)
            } catch (err) {
                console.error("Upload failed", err);
            }
        }

        setImgUrls((prev) => [...prev, ...newUrls]);
        setFileNames((prev) => [...prev, ...newNames]);
        setFileTypes((prev) => [...prev, ...newTypes]);
        setIsUploading(false);
    }

    // Skills handler functions
    const handleSelectSkills = (key: any, name: any, type: any) => {
        const allOptions = skillOptions;

        if (key === "all") {
            if (name === true) {
                // Deselect all
                setSelectedSkills([]);
            } else {
                // Select all
                setSelectedSkills(allOptions);
            }
        } else {
            setSelectedSkills((prev) => {
                const exists = prev.some((item) => item.key == key);
                if (exists) {
                    // REMOVE if already selected
                    return prev.filter((item) => item.key != key);
                } else {
                    // ADD if not selected
                    return [...prev, { key, name, type }];
                }
            });
        }
    };

    const handleRemoveSkill = (key: string) => {
        // Check if this skill is used in any NEW task (added via popup)
        const isSkillUsedInNewTasks = Object.values(memberTasks).some(tasks =>
            tasks.some(task => task.taskSkills && task.taskSkills.some((skill: any) => skill.key === key))
        );

        // Check if this skill is used in any EXISTING task (from API)
        const isSkillUsedInExistingTasks = goalData?.goal_details?.goal_details?.tasks?.some((task: any) => {
            return task.skills?.some((skill: any) => skill.skill_id == key);
        });

        if (isSkillUsedInNewTasks || isSkillUsedInExistingTasks) {
            showError('This skill is being used in one or more tasks. Please remove it from tasks first.');
            return;
        }

        setSelectedSkills((prev) => prev.filter((item) => item.key !== key));
    };

    // Get member tasks with status from actual API data
    const getMemberTasksWithStatus = (memberKey: string) => {
        if (!goalData?.goal_details?.goal_details?.tasks) {
            return [];
        }

        const tasks = goalData.goal_details.goal_details.tasks;

        // Filter tasks assigned to the specific member (considering replacements)
        const memberTasks = tasks.filter((task: any) => {
            const taskId = task.id?.toString();
            // Check if task has been reassigned locally
            if (taskReplacements[taskId]) {
                return taskReplacements[taskId] === memberKey;
            }
            // Otherwise use original assignment
            return task.assigned_to_id == memberKey;
        });

        // Map to the format expected by ReplaceMemberPopup
        return memberTasks.map((task: any) => {
            const taskId = task.id?.toString();
            const isReassigned = taskReplacements[taskId];

            return {
                id: taskId || '',
                taskName: task.title || 'Untitled Task',
                taskDescription: task.static_task_type_name || '',
                status: task.status_name,
                taskType: task.static_task_type_name,
                startDate: task.start_datetime,
                endDate: task.end_datetime,
                priority: task.priority,
                assignedToName: isReassigned ?
                    selectedMembers.find(m => m.key === isReassigned)?.name || task.assigned_to_name :
                    task.assigned_to_name,
                isReassigned: !!isReassigned
            };
        });
    };

    async function onSubmit(data: any): Promise<void> {
        // Validate skills
        if (selectedSkills?.length < 1) {
            showError('Please select at least one goal skill');
            return;
        }

        if (selectedMembers.length === 0) {
            showError('Please select at least one Goal member');
            return;
        }

        // Only include members who have tasks (same logic as UI filtering)
        const membersWithTasks = selectedMembers.filter((member: any) => {
            // Check for existing/reassigned tasks
            const memberTaskList = getMemberTasksWithStatus(member.key);
            // Check for new tasks added via popup
            const newTasks = memberTasks[member.key] || [];
            // Include member if they have either type of tasks
            return memberTaskList.length > 0 || newTasks.length > 0;
        });

        if (membersWithTasks.length === 0) {
            showError('Please assign tasks to at least one member before saving');
            return;
        }

        const keys = membersWithTasks.map((member: any) => member.key);

        // if (keys.length < 2) {
        //     showError('Please assign tasks to at least two members before saving');
        //     return
        // }

        // Prepare tasks array from memberTasks and task replacements
        const allTasks = [];

        // First, handle task replacements (existing tasks with new assignments)
        const replacementTasks = [];
        for (const [taskId, newAssigneeId] of Object.entries(taskReplacements)) {
            // Find the original task data from goalData
            const originalTask = goalData?.goal_details?.goal_details?.tasks?.find((task: any) =>
                task.id?.toString() === taskId
            );

            if (originalTask) {
                // Get existing checklists for this task
                const existingChecklists = originalTask.checklist_items?.map((item: any) => ({
                    task_id: parseInt(taskId),
                    checklist_id: parseInt(item.checklist?.id || item.id),
                    status: item.status || false,
                    created_by: parseInt(employeeID),
                })) || [];

                // Parse end date if exists
                let endDate = null;
                if (originalTask.end_datetime) {
                    const date = new Date(originalTask.end_datetime);
                    endDate = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}T23:59:59.000Z`;
                }

                // Parse start date if exists
                let startDate = null;
                if (originalTask.start_datetime) {
                    const date = new Date(originalTask.start_datetime);
                    startDate = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}T00:00:00.000Z`;
                }

                // Get existing skill IDs for this task
                const existingSkillIds = originalTask.skills?.map((skill: any) => parseInt(skill.skill_id)) || [];

                replacementTasks.push({
                    is_new_task: false,
                    existing_task_id: parseInt(taskId),
                    goal_id: parseInt(goalID),
                    static_goal_id: parseInt(goalData?.goal_details?.goal_details?.goals_static_id),
                    static_task_id: parseInt(originalTask.tasks_static_id),
                    task_title: originalTask.title,
                    task_start_date: startDate,
                    task_end_date: endDate,
                    dependency_task: originalTask.dependency_task_id ? parseInt(originalTask.dependency_task_id) : null,
                    created_by: parseInt(employeeID),
                    days: originalTask.days || 1,
                    hours: originalTask.hours || 0,
                    mins: originalTask.mins || 0,
                    checklists: existingChecklists,
                    assigned_to: parseInt(newAssigneeId),
                    skill_ids: existingSkillIds,
                });
            } else {
                console.warn(`Original task with ID ${taskId} not found for replacement`);
            }
        }

        // Then, handle new tasks from memberTasks
        for (const memberKey of keys) {
            const tasks = memberTasks[memberKey] || [];
            for (const task of tasks) {
                const checklists = task.taskChecklist.map((checklist_id: string) => ({
                    task_id: parseInt(task.taskID),
                    checklist_id: parseInt(checklist_id),
                    status: false,
                    created_by: parseInt(employeeID),
                }));

                // Parse date from MM-dd-yyyy format to yyyy-MM-dd
                let ed = null;
                if (task.endDate) {
                    const [month, day, year] = task.endDate.split('-');
                    ed = `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
                }

                const taskSkillIds = task.taskSkillID ? [parseInt(task.taskSkillID)] : [];

                allTasks.push({
                    is_new_task: true,
                    goal_id: parseInt(goalID),
                    static_goal_id: parseInt(goalData?.goal_details?.goal_details?.goals_static_id),
                    static_task_id: parseInt(task.taskID),
                    task_title: task.taskName,
                    task_start_date: task.startTaskDate ? `${task.startTaskDate}T00:00:00.000Z` : null,
                    task_end_date: ed ? `${ed}T23:59:59.000Z` : null,
                    dependency_task: task.parentTaskID ? parseInt(task.parentTaskID) : null,
                    created_by: parseInt(employeeID),
                    days: task.durationDays,
                    hours: task.durationTimeHour,
                    mins: task.durationTimeMinute,
                    checklists: checklists,
                    assigned_to: parseInt(memberKey),
                    skill_ids: taskSkillIds,
                });
            }
        }

        // Combine new tasks and replacement tasks
        allTasks.push(...replacementTasks);

        // Prepare attachments - only send new attachments for existing goals
        const existingUrls = new Set(existingAttachments.map((att) => att.file_path));
        const attachmentList = imgUrls
            ?.map((url, index) => ({
                file_name: fileNames[index],
                file_path: url,
                file_type: fileTypes[index]
            }))
            .filter((item) => !existingUrls.has(item.file_path)) || [];

        // Prepare links - send all links (API should handle existing vs new)
        const urls = links.map(l => l.url);

        // Prepare skills
        const skillIds = selectedSkills?.length > 0 ? selectedSkills?.map((s: any) => {
            return parseInt(s?.key)
        }) : [];

        // Compute removed members (original members not in current goal_members)
        const removedMembers = originalMembers
            .filter((member: any) => !keys.includes(member.key))
            .map((member: any) => member.key);


        const goalDataInput = {
            goal_title: data.goalName,
            project_id: parseInt(projectID),
            static_goal_id: parseInt(goalData?.goal_details?.goal_details?.goals_static_id),
            is_new_goal: false,
            goal_start_date: `${data.startGoalDate}T00:00:00.000Z`,
            goal_end_date: `${data.endGoalDate}T23:59:59.000Z`,
            created_by: goalData?.goal_details?.goal_details?.created_by,
            existing_goal_id: parseInt(goalID),
            description: data.goalDescription || null,
            tasks: allTasks,
            goal_members: keys,
            removed_member_ids: removedMembers,
            attachment_list: attachmentList,
            links: urls,
            skill_ids: skillIds
        }

        // Debug log to see what's being sent
        // console.log('Goal Data Input:', {
        //     ...goalDataInput,
        //     newTasks: allTasks.filter(task => task.is_new_task),
        //     replacementTasks: allTasks.filter(task => !task.is_new_task),
        //     totalTasks: allTasks.length,
        //     taskReplacements: taskReplacements
        // });

        try {
            const response = await createGoal({ variables: { goal_data: goalDataInput } });
            if (response.data.create_goal.status_code === 200) {
                showSuccess(response.data.create_goal.message)
                navigate(-1)
            }
            if (response.data.create_goal.status_code === 400) {
                showError(response.data.create_goal.message)
            }
        } catch (error) {
            if (error instanceof ApolloError) {
                showError(error.message)
            }
        }
    }

    return (
        <>
            <form onSubmit={handleSubmit(onSubmit)} id="cts-respon-p-dv" className="w-full view-goalsection-dv  relative  overflow-hidden flex flex-row items-start justify-center flex-wrap content-start py-40 px-20 mq991:p-8 mq575:p-4  box-border text-left text-base text-gray font-manrope">
                <div className="flex-1 flex flex-col items-start justify-start gap-6">
                    <BreadCumb />
                    <div className="self-stretch flex flex-row items-center justify-start gap-[5px] text-2xl">
                        <img className="w-6 relative h-6 overflow-hidden shrink-0 cursor-pointer dark-white-img" alt="" src={backArrow}
                            onClick={() => navigate(-1)}
                        />
                        <h3 className="m-0 flex-1 relative text-inherit font-bold font-inherit inline-block max-w-[calc(100%_-_29px)] mq450:text-base dark:text-white">Edit Goal</h3>
                    </div>

                    {/* Informational Note */}
                    <div className="self-stretch rounded-lg bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 p-4 flex flex-row items-start gap-3">
                        <svg className="w-5 h-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                        </svg>
                        <div className="flex-1">
                            <p className="text-sm font-semibold text-blue-800 dark:text-blue-200 my-0">Important Note</p>
                            <p className="text-sm text-blue-700 dark:text-blue-300  my-0">
                                Any changes you make (adding members, replacing tasks, editing details, etc.) will be applied when you click the <strong>Save</strong> button at the bottom of the page.
                            </p>
                        </div>
                    </div>

                    <div className="self-stretch rounded-lg bg-surface-bg-primary flex flex-col items-start justify-start gap-6  p-8 mq575:p-3 ">
                        <h5 className="my-0 text-xl-dv self-stretch relative text-lg">Goal Details</h5>
                        <div className="grid grid-cols-4 mq1400:grid-4 mq1275:grid-cols-3 mq950:grid-cols-2 mq575:grid-cols-1 mt-[8px] gap-[8px] min-w-full">
                            <InputsNew
                                label="Project Name*"
                                type='text'
                                contentPlaceholder={projectName}
                                disabled={true}
                            />
                        </div>
                        <div className="self-stretch flex flex-col items-start justify-start gap-[16px]">
                            <div className="grid grid-cols-4 mq1400:grid-4 mq1275:grid-cols-3 mq950:grid-cols-2 mq575:grid-cols-1 mt-[8px] gap-[8px] min-w-full">
                                <div className="flex flex-row items-center justify-start gap-[6px]">
                                    <input
                                        className="cursor-pointer m-0 h-4 w-4 relative overflow-hidden shrink-0"
                                        type="radio"
                                        value="existing"
                                        {...register('goal')}
                                        id="existingGoal"
                                        checked={true}
                                        disabled={true}
                                    />
                                    <label htmlFor="existingGoal" className={`cursor-pointer relative text-sm font-body-xs-semi-bold text-colors-text-black text-left inline-block min-w-[50px]`}>
                                        Existing Goal
                                    </label>
                                </div>
                                <div className="flex flex-row items-center justify-start gap-[6px]">
                                    <input
                                        className="cursor-pointer m-0 h-4 w-4 relative overflow-hidden shrink-0"
                                        type="radio"
                                        value="new"
                                        {...register('goal')}
                                        id="newGoal"
                                        disabled={true}
                                    />
                                    <label htmlFor="newGoal" className={`cursor-pointer relative text-sm font-body-xs-semi-bold text-colors-text-black text-left inline-block min-w-[50px]`}>
                                        New Goal
                                    </label>
                                </div>
                            </div>
                            {errors?.goal && (
                                <p className="text-red p-2 cts-bg-error my-0 flex items-center gap-[4px] ctcerror-width-blg">
                                    <img src={alertIcon} alt="" />
                                    {typeof errors.goal.message === 'string' ? errors.goal.message : 'Invalid input'}
                                </p>
                            )}

                            {/* {errors?.goal && <p className="text-red p-2 cts-bg-error my-0 flex items-center gap-[4px] ctcerror-width-blg"><img src={alertIcon} alt="" />{errors?.goal.message}</p>} */}
                        </div>
                        <div className="grid grid-cols-4 mq1400:grid-4 mq1275:grid-cols-3 mq950:grid-cols-2 mq575:grid-cols-1 mt-[8px] gap-[8px] min-w-full">
                            <InputsNew
                                label="Goal Type*"
                                type='text'
                                contentPlaceholder={goalData?.goal_details?.goal_details?.static_goal_type_name}
                                disabled={true}
                            />
                            <InputsNew
                                label="Goal Name/Label*"
                                type='text'
                                contentPlaceholder="Goal Name"
                                register={register('goalName')}
                                errorMessage={errors.goalName}
                            />
                            <div className="flex flex-col items-start justify-start gap-[8px] startdate-inner-input">
                                <Controller
                                    name="startGoalDate"
                                    control={control}
                                    render={({ field }) => (
                                        <DateSelector
                                            label="Start Date*"
                                            selectedDate={field.value}
                                            onChange={(date: string | null) => {
                                                const endDate = watch('endGoalDate');

                                                if (endDate && date && new Date(date) > new Date(endDate)) {
                                                    showError('End date cannot be earlier than start date.');
                                                    trigger('startGoalDate');
                                                    return;
                                                }

                                                field.onChange(date);
                                                setValue('startGoalDate', date);
                                                trigger('startGoalDate');
                                            }}
                                            error={errors.startGoalDate?.message}
                                            nothideParsedValue={true}
                                            disabled={true}
                                        // maxDate={new Date()}
                                        />
                                    )}
                                />
                            </div>

                            <div className="flex flex-col items-start justify-start gap-[8px] startdate-inner-input">
                                <Controller
                                    name="endGoalDate"
                                    control={control}
                                    render={({ field }) => (
                                        <DateSelector
                                            label="End Date*"
                                            selectedDate={field.value}
                                            onChange={(date: string | null) => {
                                                const startDate = watch('startGoalDate');

                                                if (startDate && date) {
                                                    const start = new Date(startDate);
                                                    const end = new Date(date);

                                                    if (end < start) {
                                                        showError('End date cannot be earlier than start date.');
                                                        trigger('endGoalDate');
                                                        return;
                                                    }

                                                    const diffDays = differenceInDays(end, start);
                                                    // if (diffDays > 15) {
                                                    //     showError('End date cannot be more than 15 days after the start date.');
                                                    //     trigger('endGoalDate');
                                                    //     return;
                                                    // }
                                                }

                                                // if (startDate && date && new Date(date) < new Date(startDate)) {
                                                //     showError('End date cannot be earlier than start date.');
                                                //     trigger('endGoalDate');
                                                //     return;
                                                // }

                                                field.onChange(date);
                                                setValue('endGoalDate', date);
                                                trigger('endGoalDate');
                                            }}
                                            error={errors.endGoalDate?.message}
                                            nothideParsedValue={true}
                                            // disabled={!isEndDateEditable}
                                        // maxDate={new Date()}
                                        />
                                    )}
                                />
                                {/* {!isEndDateEditable && (
                                    <p className="text-xs text-orange-600 dark:text-orange-400 mt-1">
                                        End date cannot be changed because one or more tasks have already started or completed.
                                    </p>
                                )} */}
                            </div>
                        </div>
                        <div className="self-stretch flex flex-col items-start justify-start text-sm">
                            <div className="self-stretch rounded-radius-md  overflow-hidden flex flex-row items-start justify-start pt-2 shadow-[0px_1px_2px_rgba(16,_24,_40,_0.05)] ">
                                <InputsNew
                                    contentPlaceholder="Goal Description"
                                    label="Goal Description"
                                    type="textarea"
                                    register={register('goalDescription')}
                                    errorMessage={errors.goalDescription}
                                />
                            </div>
                        </div>

                        {/* Skills Section */}
                        <div className="grid grid-cols-4 mq1400:grid-4 mq1275:grid-cols-3 mq950:mq1275:grid-cols-2 mq575:mq1275:grid-cols-1 gap-[8px] min-w-full">
                            <div className="grid">
                                <InputsDropdownMultipleSearchSelect
                                    label="Skills"
                                    placeHolder="Choose"
                                    options={skillOptions}
                                    onSelect={handleSelectSkills}
                                    inputType="checkbox"
                                    checkedItems={selectedSkills}
                                />

                                {selectedSkills?.length > 0 && (
                                    <div className="reportmulselect-blg flex-1 w-full cts-flex1-remd contents flex-col items-start justify-start text-left text-sm mq825:max-w-[100%] text-text-text-primary font-body-reg min-w-full">
                                        <div className="inner-reportmulselect-blg items-start justify-start text-left text-sm mq825:max-w-[100%] text-text-text-primary font-body-reg mt-2">
                                            {selectedSkills.map((ss: any) => (
                                                <div
                                                    key={ss?.key}
                                                    className="reportmulselect-blg-dv rounded-radius-md1 overflow-hidden flex flex-row items-center justify-start gap-[8px] inner-item-select-dv border-[1px] border-solid border-surface-border dark:bg-slate-700"
                                                >
                                                    <div className="flex flex-row items-center justify-start">
                                                        <div className="relative text-sm font-body-xs-semi-bold text-colors-text-black text-left inline-block dark:text-white">
                                                            {ss?.name}
                                                        </div>
                                                    </div>
                                                    <img
                                                        className="h-4 w-4 relative overflow-hidden shrink-0 cursor-pointer"
                                                        loading="lazy"
                                                        alt="Remove"
                                                        src={closeDowpDowniconimg}
                                                        onClick={() => handleRemoveSkill(ss.key)}
                                                    />
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className="flex-1 flex flex-col items-start justify-start gap-4 w-full edir_goal_btn_dv">
                            <div className="flex items-center justify-between w-full">
                                <div className="flex flex-col">
                                    <label className="text-sm font-semibold dark:text-white">Goal Members</label>
                                    <span className="text-xs text-gray-500 dark:text-gray-400">
                                        Original members cannot be removed
                                    </span>
                                    {resourceOverviewLoading ? (
                                        <div className="flex items-center gap-2 mt-1">
                                            <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-blue-600"></div>
                                            <span className="text-xs text-gray-600 dark:text-gray-400">Loading member availability...</span>
                                        </div>
                                    ) : goalMemberOptions.length > 0 && (
                                        <div className="mt-1 text-xs text-gray-600 dark:text-gray-400">
                                            {(() => {
                                                const availableCount = goalMemberOptions.filter(m => m.isAvailable).length;
                                                const totalCount = goalMemberOptions.length;
                                                return (
                                                    <span>
                                                        <span className="text-green-600 dark:text-green-400 font-semibold">{availableCount} available</span>
                                                        {' • '}
                                                        <span className="text-red-600 dark:text-red-400 font-semibold light-red-blg-dv">{totalCount - availableCount} not available</span>
                                                        {' of '}{totalCount} total members
                                                    </span>
                                                );
                                            })()}
                                        </div>
                                    )}
                                </div>
                                <button
                                    type="button"
                                    onClick={() => setShowAddMemberPopup(true)}
                                    className="py-2.5 h-[44px] mq575:h-[35px] mq575:py-[12.5px] mq575:px-[25px] text-sm block cursor-pointer flex-col items-center justify-start px-[20px] plus-task-btn taskdash-plush-img rounded gap-[4px]  text-white"
                                >
                                    Add Member/Task
                                </button>
                            </div>

                            {(() => {
                                const membersWithTasks = selectedMembers?.filter((member: any) => {
                                    // Check for existing/reassigned tasks
                                    const memberTaskList = getMemberTasksWithStatus(member.key);
                                    // Check for new tasks added via popup
                                    const newTasks = memberTasks[member.key] || [];
                                    // For original members, show them even if task data hasn't loaded yet
                                    const isOriginalMember = originalMembers.some(original => original.key === member.key);
                                    // Show member if they have tasks OR if they are original members
                                    return memberTaskList.length > 0 || newTasks.length > 0 || isOriginalMember;
                                }) || [];
                                return membersWithTasks.length > 0;
                            })() ? (
                                <div className="w-full border border-gray-300 dark:border-gray-600 rounded-md py-4">
                                    <div className="grid gap-2">
                                        {selectedMembers
                                            .filter((member: any) => {
                                                // Check for existing/reassigned tasks
                                                const memberTaskList = getMemberTasksWithStatus(member.key);
                                                // Check for new tasks added via popup
                                                const newTasks = memberTasks[member.key] || [];
                                                // For original members, show them even if task data hasn't loaded yet
                                                const isOriginalMember = originalMembers.some(original => original.key === member.key);
                                                // Show member if they have tasks OR if they are original members
                                                return memberTaskList.length > 0 || newTasks.length > 0 || isOriginalMember;
                                            })
                                            .map((member: any) => {
                                                const memberTaskList = getMemberTasksWithStatus(member.key);
                                                const newTasksList = memberTasks[member.key] || [];

                                                // Total tasks = API tasks + new tasks
                                                const totalTaskCount = memberTaskList.length + newTasksList.length;
                                                const hasTasks = totalTaskCount > 0;

                                                const isRemovable = isRemovableMember(member.key);
                                                const isOriginalMember = !isRemovable;

                                                // Check if member has NEW tasks (added via popup) - only these should be editable
                                                const hasNewTasks = newTasksList.length > 0;

                                                // Check if member has reassigned tasks
                                                const hasReassignedTasks = memberTaskList.some((task: any) => task.isReassigned);

                                                return (
                                                    <div
                                                        key={member.key}
                                                        className={`flex edittask-section items-center justify-between p-3 bg-gray-50 dark:bg-gray-800 rounded border ${hasNewTasks ? 'cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700' : ''}`}
                                                        onClick={() => hasNewTasks && handleMemberClick(member)}
                                                    >
                                                        <div className="flex items-center gap-3">
                                                            <div className={`w-8 h-8 ${hasNewTasks ? 'bg-green-500' :
                                                                hasReassignedTasks ? 'bg-blue-500' :
                                                                    isOriginalMember ? 'bg-gray-500' : 'bg-blue-500'
                                                                } text-white rounded-full flex items-center justify-center text-sm font-semibold relative`}>
                                                                {member.name.charAt(0).toUpperCase()}
                                                                {hasNewTasks && (
                                                                    <div className="absolute -top-1 -right-1 w-3 h-3 bg-green-600 rounded-full flex items-center justify-center">
                                                                        <span className="text-xs text-white">✓</span>
                                                                    </div>
                                                                )}
                                                            </div>
                                                            <div className="flex flex-col">
                                                                <div className="flex items-center gap-2">
                                                                    {member.isAvailable !== undefined && (
                                                                        <div className={`w-2 h-2 rounded-full ${member.isAvailable ? 'bg-green-500' : 'bg-red-500'
                                                                            }`}></div>
                                                                    )}
                                                                    <span className="text-sm font-medium dark:text-white">
                                                                        {member.name}
                                                                    </span>
                                                                    {isOriginalMember && (
                                                                        <span className="text-xs bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200 px-2 py-1 rounded">
                                                                            Original
                                                                        </span>
                                                                    )}
                                                                    {member.isAvailable !== undefined && (
                                                                        <span className={`text-xs px-2 py-1 rounded ${member.isAvailable
                                                                            ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                                                                            : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                                                                            }`}>
                                                                            {member.isAvailable ? 'Available' : 'Not Available'}
                                                                        </span>
                                                                    )}
                                                                </div>
                                                                {member.availableHours !== undefined && member.totalHours !== undefined && (
                                                                    <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                                                        {member.availableHours.toFixed(1)}h / {member.totalHours.toFixed(1)}h available
                                                                        {member.utilizationPercentage !== undefined && (
                                                                            <span className="ml-2">
                                                                                ({member.utilizationPercentage.toFixed(0)}% utilized)
                                                                            </span>
                                                                        )}
                                                                    </div>
                                                                )}
                                                                {hasTasks ? (
                                                                    <span className={`text-xs ${hasNewTasks ? 'text-green-600 dark:text-green-400' : 'text-gray-600 dark:text-gray-400'}`}>
                                                                        {totalTaskCount} task{totalTaskCount !== 1 ? 's' : ''} assigned
                                                                        {hasReassignedTasks && (
                                                                            <span className="ml-1 px-1 py-0.5 bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 rounded text-xs">
                                                                                • Reassigned
                                                                            </span>
                                                                        )}
                                                                        {hasNewTasks && <span> • Click to edit</span>}
                                                                    </span>
                                                                ) : (
                                                                    <span className="text-xs text-gray-500 dark:text-gray-400">
                                                                        {isOriginalMember ? 'Existing member' : 'No tasks assigned'}
                                                                    </span>
                                                                )}
                                                            </div>
                                                        </div>
                                                        <div className="flex items-center gap-2">
                                                            {/* Replace Member Button - Only show if member has replaceable tasks */}
                                                            {(() => {
                                                                const memberTaskList = getMemberTasksWithStatus(member.key);
                                                                const replaceableTasks = memberTaskList.filter((task: any) =>
                                                                    task.status === 'Not Started' &&
                                                                    task.taskType !== 'Meeting / Knowledge Transfer'
                                                                );
                                                                // const memberTasksCount = memberTaskList.length;
                                                                return replaceableTasks.length > 0 ? (
                                                                    <button
                                                                        type="button"
                                                                        onClick={(e) => {
                                                                            e.stopPropagation();
                                                                            handleReplaceMember(member);
                                                                        }}
                                                                        className="text-blue-500 hover:text-blue-700  replace-btn-dv"
                                                                        title={`Replace member (${replaceableTasks.length} replaceable task${replaceableTasks.length !== 1 ? 's' : ''})`}
                                                                    >
                                                                        <img
                                                                            className="h-5 w-5 grayscale"
                                                                            src={rePlacement}
                                                                            alt="Cannot remove"
                                                                        />
                                                                    </button>
                                                                ) : null;
                                                            })()}

                                                            {/* Remove Member Button */}
                                                            {(() => {
                                                                const hasReassignedTasks = Object.values(taskReplacements).includes(member.key);

                                                                if (isRemovable) {
                                                                    let tooltipText = "Remove member";
                                                                    let buttonClass = "text-red-500 hover:text-red-700 p-1";

                                                                    if (hasReassignedTasks) {
                                                                        const reassignedCount = Object.values(taskReplacements).filter(id => id === member.key).length;
                                                                        tooltipText = `Remove member (will revert ${reassignedCount} reassigned task${reassignedCount !== 1 ? 's' : ''})`;
                                                                        buttonClass = "text-orange-500 hover:text-orange-700 p-1";
                                                                    }

                                                                    return (
                                                                        <button
                                                                            type="button"
                                                                            onClick={(e) => {
                                                                                e.stopPropagation();
                                                                                handleRemoveMember(member.key);
                                                                            }}
                                                                            className="bg-crimson right_status_clockout flex flex-row items-center justify-center py-1 px-0 rounded-radius-md  text-sm text-white"
                                                                            title={tooltipText}
                                                                        >
                                                                            <img
                                                                                className="h-4 w-4 grayscale"
                                                                                src={closeIcon}
                                                                                alt="Cannot remove"
                                                                            />

                                                                        </button>
                                                                    );
                                                                } else {
                                                                    return (
                                                                        <div className=" bg-crimson lightgray_status_btn flex flex-row items-center justify-center py-1 px-0 rounded-radius-md  text-sm text-white" title="Cannot remove original member">
                                                                            <img
                                                                                className="h-4 w-4 grayscale"
                                                                                src={closeIcon}
                                                                                alt="Cannot remove"
                                                                            />
                                                                        </div>
                                                                    );
                                                                }
                                                            })()}
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                    </div>
                                </div>
                            ) : (
                                <div className="w-full border border-gray-300 dark:border-gray-600 rounded-md p-4 text-center">
                                    {goalDataLoading ? (
                                        <>
                                            <div className="flex items-center justify-center gap-2 mb-2">
                                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                                                <span className="text-sm text-gray-600 dark:text-gray-400">Loading goal details...</span>
                                            </div>
                                        </>
                                    ) : (
                                        <>
                                            <p className="text-gray-500 dark:text-gray-400 text-sm">
                                                No members with tasks assigned to this goal
                                            </p>
                                            <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                                                Add members and assign tasks to see them here
                                            </p>
                                        </>
                                    )}
                                </div>
                            )}
                        </div>
                        {/* <div className="flex min-w-full selectall_header_bg">
                            <input
                                type="checkbox"
                                id="goalDescriptionCheckbox"
                                
                            />
                            <label htmlFor="goalDescriptionCheckbox" className="font-semibold">Goal Description</label>
                        </div> */}
                        {/* Pending Changes Summary */}
                        {Object.keys(taskReplacements).length > 0 && (
                            <div className="mb-4 p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
                                <h4 className="text-sm font-semibold text-blue-800 dark:text-blue-200 mb-2">
                                    Pending Task Reassignments ({Object.keys(taskReplacements).length})
                                </h4>
                                <div className="text-xs text-blue-700 dark:text-blue-300">
                                    {Object.entries(taskReplacements).map(([taskId, newAssigneeId]) => {
                                        const task = goalData?.goal_details?.goal_details?.tasks?.find((t: any) => t.id?.toString() === taskId);
                                        const newAssignee = selectedMembers.find(m => m.key === newAssigneeId);
                                        const originalAssignee = goalData?.goal_details?.goal_details?.tasks?.find((t: any) => t.id?.toString() === taskId)?.assigned_to_name;

                                        return (
                                            <div key={taskId} className="mb-1">
                                                "{task?.title}" → {newAssignee?.name} (was: {originalAssignee})
                                            </div>
                                        );
                                    })}
                                </div>
                                <p className="text-xs text-blue-600 dark:text-blue-400 mt-2">
                                    These changes will be applied when you save the goal.
                                </p>
                            </div>
                        )}

                        {/* Knowledge Base Section */}
                        <h5 className="my-0 text-xl-dv self-stretch relative text-lg dark:text-white">Knowledge Base</h5>

                        <div className="knowledge_base_section p-4 max-w-full flex flex-col self-stretch ">
                            <div className="w-full overflow-hidden flex flex-row items-center justify-between gap-0 text-lg">
                                <div className="tabsection_goalview w-full relative rounded-md bg-whitesmoke border-gainsboro border-solid border-b-[1px] box-border flex flex-row items-center justify-start p-1 text-center text-base text-dimgray font-manrope">
                                    <div
                                        onClick={() => setGoalAttachmentView("attachments")}
                                        className={`cursor-pointer flex-1 rounded flex flex-row items-center justify-center p-4 ${goalAttachmentView === "attachments" ? 'bg-white' : ''}`}
                                    >
                                        <div className="relative">Attachments ({imgUrls && imgUrls?.length})</div>
                                    </div>
                                    <div
                                        onClick={() => setGoalAttachmentView("links")}
                                        className={`cursor-pointer flex-1 shadow-[0px_1px_2px_rgba(0,_0,_0,_0.05)] rounded flex flex-row items-center justify-center p-4 text-gray ${goalAttachmentView === "links" ? 'bg-white' : ''} `}
                                    >
                                        <div className="relative font-semibold">Links ({links && links?.length})</div>
                                    </div>
                                </div>
                            </div>
                            <hr className="bdr-custom-taskcrad" />

                            {isUploading && (
                                <Loader />
                            )}

                            {validationMessage && (
                                <div className="text-red-500 text-sm mt-2 p-2 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded">
                                    {validationMessage}
                                </div>
                            )}

                            {goalAttachmentView === "attachments" && (
                                <div className="max-w-full flex-col self-stretch grid grid-cols-4 gap-4 mt-6">
                                    <div className="upload-viewidea-box col-span-1 grid text-center gap-[8px] mx-center">
                                        <div className="w-full flex justify-center">
                                            <svg xmlns="http://www.w3.org/2000/svg" width="22" height="21" viewBox="0 0 22 21" fill="none" className="relative left-[5px]">
                                                <path d="M11 10.4999V19.4999M11 10.4999L8.49996 12.4999M11 10.4999L13.5 12.4999M4.03396 7.61694C3.08817 7.85494 2.26184 8.4301 1.71021 9.23438C1.15859 10.0387 0.919644 11.0167 1.03827 11.9847C1.15689 12.9528 1.62492 13.8442 2.35443 14.4914C3.08393 15.1387 4.02469 15.4974 4.99996 15.4999H5.99996" stroke="#191D23" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                                <path d="M14.8299 5.63796C14.4881 4.28453 13.6445 3.1117 12.47 2.35722C11.2956 1.60274 9.87819 1.32305 8.50517 1.57487C7.13215 1.82668 5.90625 2.59115 5.07598 3.7133C4.2457 4.83545 3.87318 6.23133 4.03392 7.61796C4.03392 7.61796 4.18692 8.49996 4.49992 8.99996" stroke="#191D23" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                                <path d="M16 15.5C16.706 15.4995 17.404 15.3495 18.0479 15.0599C18.6917 14.7702 19.267 14.3475 19.7357 13.8195C20.2045 13.2915 20.5561 12.6702 20.7674 11.9965C20.9787 11.3229 21.045 10.6121 20.9618 9.91094C20.8786 9.20982 20.6479 8.53427 20.2848 7.92874C19.9217 7.32321 19.4345 6.80145 18.8552 6.39778C18.276 5.99412 17.6178 5.71772 16.924 5.58676C16.2302 5.4558 15.5166 5.47327 14.83 5.638L13.5 6" stroke="#191D23" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                            </svg>
                                        </div>
                                        <p className="my-0 relative left-[5px]">Upload</p>
                                        <div className="flex justify-center max-w-full relative">
                                            <button
                                                type="button"
                                                onClick={handleIconClick}
                                                className="ml-2 flex items-center justify-center w-8 h-8 cursor-pointer flex flex-col items-start justify-start p-[10px] plus-task-btn taskdash-plush-img rounded"
                                            >
                                                <img
                                                    className="w-4 h-4 relative overflow-hidden shrink-0 cursor-pointer"
                                                    loading="lazy"
                                                    alt=""
                                                    src={ioniconaaddoutline}
                                                />
                                            </button>
                                        </div>
                                        <input
                                            type="file"
                                            ref={fileInputRef}
                                            style={{ display: "none" }}
                                            onChange={handleFileUpload}
                                            multiple
                                        />
                                    </div>
                                    <div className="upload-viewidea-right col-span-3">
                                        <div className="grid grid-cols-3 mq1400:grid-3 mq1356:grid-2 mq1275:grid-cols-2 mq950:grid-cols-2 mq575:grid-cols-1 gap-[4px] min-w-full">
                                            {imgUrls?.map((url, index) => {
                                                const existing = existingAttachments?.find((att) => att.file_path === url);

                                                return (
                                                    <a
                                                        key={url}
                                                        href={url}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="cursor-pointer upload-viewidea-item gap-[4px] flex items-start relative"
                                                    >
                                                        <div className="upload-viewidea-file flex justify-center items-center">
                                                            <img src={viewFileIon} alt="" />
                                                        </div>
                                                        <div className="upload-viewidea-content">
                                                            <p className="text-black dark:text-white my-0 font-bold word">
                                                                {existing?.file_name || fileNames[index]}
                                                            </p>
                                                            <p className="my-0 text-black dark:text-white font-normal">
                                                                {existing?.file_type || fileTypes[index]}
                                                            </p>
                                                        </div>
                                                    </a>
                                                );
                                            })}
                                        </div>
                                    </div>
                                </div>
                            )}

                            {goalAttachmentView === "links" && (
                                <div className="max-w-full flex-col self-stretch grid grid-cols-4 gap-4 mt-6">
                                    <LinkManager links={links} onLinksChange={setLinks} />
                                </div>
                            )}
                        </div>

                        <div className="self-stretch flex flex-row items-center justify-end py-0 pr-0 gap-[8px] mq950:box-border mq1425:flex-wrap  mq1425:box-border mq450:box-border">
                            <button className="cursor-pointer bg-white py-2.5 h-[44px] mq575:h-[35px] px-[42px] btn-text-purple mq575:py-[12.5px] mq575:px-[25px] rounded-radiuscomponent-button box-border flex flex-row items-center justify-center gap-[12px] border-[2px] border-solid border-surface-primary-surface hover:bg-gainsboro-100 hover:box-border hover:border-[2px] hover:border-solid hover:border-dodgerblue"
                                type="button"
                                onClick={() => navigate(-1)}
                            >
                                <div className="relative text-sm font-body-xs-semi-bold  dark:text-white text-center inline-block">
                                    Cancel
                                </div>
                            </button>
                            <button className={`cursor-pointer [border:none] py-2.5 h-[44px] mq575:h-[35px] px-[42px] mq575:py-[12.5px] mq575:px-[25px] bg-surface-primary-surface flex flex-row items-center justify-center gap-[12px] hover:bg-dodgerblue`}
                                // disabled={(err || careDelErr) !== ''}
                                type="submit"
                            >
                                <div className="relative text-sm font-body-xs-semi-bold text-surface-bg-primary text-center inline-block">
                                    Save
                                </div>
                            </button>
                        </div>
                    </div>
                </div>

            </form>
            {(createGoalLoading || goalDataLoading || goalsDataLoading || resourceOverviewLoading || skillLoading) && (
                <Loader />
            )}

            {showAddMemberPopup && (
                <AddMemberToGoalPopup
                    isOpen={showAddMemberPopup}
                    onClose={() => {
                        setShowAddMemberPopup(false);
                        setEditingMember(null);
                    }}
                    onAddMember={handleAddMember}
                    availableMembers={getAvailableMembers()}
                    goalTypeData={goalTypeData}
                    goalStartDate={watch('startGoalDate')}
                    goalEndDate={watch('endGoalDate')}
                    editingMember={editingMember}
                    existingTasks={editingMember ? memberTasks[editingMember.key] : undefined}
                    allProjectMembers={goalMemberOptions}
                    existingGoalMembers={selectedMembers}
                    goalSkills={selectedSkills}
                    parentTaskOptions={parentTaskOptions}
                />
            )}

            {showReplaceMemberPopup && memberToReplace && (
                <ReplaceMemberPopup
                    isOpen={showReplaceMemberPopup}
                    onClose={() => {
                        setShowReplaceMemberPopup(false);
                        setMemberToReplace(null);
                    }}
                    onReplaceMember={handleReplaceMemberSubmit}
                    memberToReplace={memberToReplace}
                    memberTasks={getMemberTasksWithStatus(memberToReplace.key)}
                    allProjectMembers={goalMemberOptions}
                    existingGoalMembers={selectedMembers}
                />
            )}

            {showRemoveConfirmation && memberToRemove && (
                <NotifyPopup
                    showMessage={
                        <div>
                            <div className="mb-3">
                                <strong>{memberToRemove.name}</strong> has <strong>{memberToRemove.reassignedTaskCount}</strong> reassigned task{memberToRemove.reassignedTaskCount !== 1 ? 's' : ''}.
                            </div>
                            <div className="text-sm text-gray-600 dark:text-gray-400">
                                Do you want to revert these tasks to their original assignees and remove the member?
                            </div>
                        </div>
                    }
                    msgHeading="Remove Member with Reassigned Tasks"
                    onsubmit={handleConfirmRemoveMember}
                    back={handleCancelRemoveMember}
                    notDelete={false}
                    isDelete={true}
                />
            )}
        </>
    );
};

export default EditGoal;
